import { FC } from "react";
import Input from "shared/Input/Input";
import Textarea from "shared/Textarea/Textarea";
import Select from "shared/Select/Select";
import CommonLayout from "./CommonLayout";
import FormItem from "./FormItem";
import { useDispatch, useSelector } from "react-redux";
import { clearHolidayPackage, selectCurrentHolidayPackage, setHolidayPackage } from "application/features/holidayPackage/holidayPackageSlice";
import Label from "components/Label/Label";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import type { HolidayPackage } from "data/types/holidayPackage";

export interface AddHolidayPackage1Props { }

const PageAddHolidayPackage1: FC<AddHolidayPackage1Props> = () => {

    const { t, ready } = useTranslation("translation");
    const holidayPackage: HolidayPackage = useSelector(selectCurrentHolidayPackage)
    const navigate = useNavigate();

    const dispatch = useDispatch()

    const handleUpdateProperty = async (field: string, value: any) => {
        //
        if (field === "duration_days") {
            value = parseInt(value);
        }
        if (field === "tags") {
            // Split tags by comma and trim whitespace
            value = value.split(",").map(tag => tag.trim()).join(",");
        }

        dispatch(setHolidayPackage({ ...holidayPackage, [field]: value }))
    };

    // WEB-197: the user had no way to delete an in-progress package draft.
    // Discard the persisted draft and return to the host's listing page.
    // NOTE: do not re-populate Redux on mount — the form's inputs handle
    // undefined values with `||` fallbacks, and dispatching defaults here
    // would re-introduce a draft into Redux immediately after a clear.
    const handleDiscardDraft = () => {
        dispatch(clearHolidayPackage());
        navigate("/host/my-properties");
    };

    function nextPageCondition() {
        return holidayPackage?.name?.length > 0 && holidayPackage?.duration_days >= 1
    }

    const moodSelection: { [key: string]: { label: string; description: string } } = t(
        "translation:components.moods", 
        { returnObjects: true }
    );

    if (!ready) return null;

    return (
        <CommonLayout
            index="01"
            backtHref="/host/new-holiday-package/add-listing-1"
            nextHref={"/host/new-holiday-package/add-listing-2"}
            canGoNext={nextPageCondition()}
        >
        <>
            {holidayPackage && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 px-4 py-3 mb-2">
                    <p className="text-sm text-amber-900 dark:text-amber-100">
                        <strong>Hai una bozza in corso.</strong>{" "}
                        I campi qui sotto sono pre-compilati con i dati della sessione precedente.
                        Puoi continuare a modificarli oppure scartare la bozza per ripartire da zero.
                    </p>
                </div>
            )}
            <h2 className="text-2xl font-semibold">{t("translation:components.holidayPackageForm.page1.title")}</h2>
            <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
            {/* FORM */}
            <div className="space-y-8">
            {/* ITEM */}
            <FormItem
                label={t("translation:components.holidayPackageForm.page1.form.packageName")}
                desc={""}
            >
                <div className="my-2">
                    <Label>{t("translation:components.holidayPackageForm.page1.form.packageNameDescription")}</Label>
                </div>
                <Input
                    value={holidayPackage?.name || ""}
                    onChange={(e) => handleUpdateProperty("name", e.target.value)}
                    placeholder={t("translation:components.holidayPackageForm.page1.form.packageNameLabel")}
                />
            </FormItem>

            <FormItem
                label={t("translation:components.holidayPackageForm.page1.form.packageDescription")}
                desc={t("translation:components.holidayPackageForm.page1.form.packageDescriptionRules")}
            >
                <div className="my-2">
                    <Label>{t("translation:components.holidayPackageForm.page1.form.packageDescriptionDescription")}</Label>
                </div>
                <Textarea
                    value={holidayPackage?.description || ""}
                    onChange={(e) => handleUpdateProperty("description", e.target.value)}
                    placeholder={t("translation:components.holidayPackageForm.page1.form.packageDescriptionPlaceholder")}
                    rows={6}
                    className="mt-2"
                    maxLength={5000}
                />
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2">
                    {holidayPackage?.description?.length || 0} / 5000 {t("translation:components.holidayPackageForm.page1.form.characters")}
                </p>
            </FormItem>

            <FormItem
                label={t("translation:components.holidayPackageForm.page1.form.packageTags")}
                desc={t("translation:components.holidayPackageForm.page1.form.packageTagsRules")}
            >
                <div className="my-2">
                    <Label>{t("translation:components.holidayPackageForm.page1.form.packageTagsDescription")}</Label>
                </div>
                <Input
                    value={holidayPackage?.tags || ""}
                    onChange={(e) => handleUpdateProperty("tags", e.target.value)}
                />
                <div className="my-2">
                    {
                        holidayPackage?.tags && holidayPackage?.tags.length > 0 ? (
                            holidayPackage?.tags.split(",").map((tag, index) => (
                                <span
                                    key={tag.trim() || `tag-${index}`}
                                    className="inline-block bg-neutral-200 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200 rounded-full px-3 py-1 mr-2 mb-2 text-sm"
                                >
                                    {tag.trim()}
                                </span>
                            ))
                        ) : null
                    }
                </div>
            </FormItem>

            <FormItem
                label={t("translation:components.holidayPackageForm.page1.form.packageDuration")}
                desc={t("translation:components.holidayPackageForm.page1.form.packageDurationDescription")}
            >
                <Input
                    value={holidayPackage?.duration_days || 1}
                    onChange={(e) => handleUpdateProperty("duration_days", e.target.value)}
                    type = "number"
                />
            </FormItem>

            <FormItem
                label={t("translation:components.holidayPackageForm.page1.form.mood")}
                desc={t("translation:components.holidayPackageForm.page1.form.moodDescription")}
            >
                <div className="my-2">
                    <Label>{t("translation:components.holidayPackageForm.page1.form.moodExplanation")}</Label>
                </div>
                <Select
                    value={holidayPackage?.mood || "business"}
                    onChange={(e) => handleUpdateProperty("mood", e.target.value)}
                >
                    <option value="business">
                        {`${moodSelection.business.label}: ${moodSelection.business.description}`}
                    </option>
                    <option value="comfort">
                        {`${moodSelection.comfort.label}: ${moodSelection.comfort.description}`}
                    </option>
                    <option value="authentic">
                        {`${moodSelection.authentic.label}: ${moodSelection.authentic.description}`}
                    </option>
                </Select>
            </FormItem>
            </div>

            {/* WEB-197: discard the in-progress package draft */}
            <div className="mt-12 pt-6 border-t border-neutral-200 dark:border-neutral-700">
                <ButtonSecondary onClick={handleDiscardDraft}>
                    Annulla e elimina bozza
                </ButtonSecondary>
            </div>
        </>
        </CommonLayout >
    );
};

export default PageAddHolidayPackage1;
